import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, UserPlus, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLE_LABEL = { OWNER: "Owner", STAFF: "Staff", VIEWER: "CA / Viewer" };

/** Public page — the invitee is not signed in, and may not have an account. */
export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", password: "" });

  useEffect(() => {
    api
      .get(`/public/invitations/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { token };
      if (!invite.for_existing_user) {
        payload.name = form.name;
        payload.password = form.password;
      }
      const { data } = await api.post("/public/invitations/accept", payload);
      toast.success(`You have joined ${data.organization.name}`);
      // The accept response sets the session cookie, so go straight in.
      window.location.assign("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-950 px-4">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-xl bg-rose-500/15 text-rose-400">
            <AlertTriangle size={22} />
          </div>
          <h1 className="font-heading text-xl font-bold text-white">Invitation unavailable</h1>
          <p className="mt-2 text-sm text-slate-400" data-testid="invite-error">{error}</p>
          <Button onClick={() => navigate("/login")} className="mt-6 bg-blue-600 hover:bg-blue-700">
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-950">
        <div className="text-sm text-slate-400">Checking your invitation…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-950 px-4" data-testid="accept-invite-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-xl bg-blue-600/15 text-blue-400">
            <UserPlus size={22} />
          </div>
          <div className="font-heading text-2xl font-bold text-white">
            Join {invite.organization_name}
          </div>
          <p className="mt-2 text-sm text-slate-400">
            {invite.invited_by_name} invited <span className="text-slate-200">{invite.email}</span> as{" "}
            <span className="text-blue-400">{ROLE_LABEL[invite.role] || invite.role}</span>.
          </p>
        </div>

        <form onSubmit={accept} className="space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="accept-invite-form">
          {invite.for_existing_user ? (
            <p className="text-xs text-slate-400">
              You already have an ERP4U account for this email. Accepting adds this organisation to
              it — you can switch between them from the sidebar.
            </p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Your name</Label>
                <Input
                  data-testid="accept-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="border-white/10 bg-white/5 text-white"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-400">Choose a password</Label>
                <Input
                  data-testid="accept-password"
                  type="password"
                  autoComplete="new-password"
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  className="border-white/10 bg-white/5 text-white"
                  required
                />
                <p className="text-[11px] text-slate-500">At least 6 characters.</p>
              </div>
            </>
          )}

          <Button type="submit" disabled={busy} data-testid="accept-submit"
            className="w-full gap-1.5 bg-blue-600 hover:bg-blue-700">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
            {invite.for_existing_user ? "Join organisation" : "Create account and join"}
          </Button>
        </form>
      </div>
    </div>
  );
}
